import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import CommandLayout from "../../components/war-room/CommandLayout";
import { useCaseAuthorities, type Authority } from "../../hooks/useCaseAuthorities";
import { useBuildShieldWall } from "../../hooks/useBuildShieldWall";
import AuthorityInfographic from "../../components/war-room/AuthorityInfographic";
import { useLex } from "../../lib/theme/ThemeProvider";

/**
 * The Shield Wall — binding authorities (contracts, trusts, licenses) drawn as
 * walls across time. Grants are collapsed into bands by instrument type; click a
 * band to see its grants, click a grant for its gatehouse / countdown detail.
 * A grant is greyed when EXPIRED (its window lapsed or a trigger ended it).
 */
function ShieldWall() {
  const { caseId } = useParams<{ caseId: string }>();
  const lex = useLex();
  const { data: auths, isLoading, error } = useCaseAuthorities(caseId);
  const [openType, setOpenType] = useState<string | null>(null);
  const [focusId, setFocusId] = useState<string | null>(null);
  const { build, building, error: buildError, status } = useBuildShieldWall(caseId);
  const newSince = status?.new_instruments_since ?? 0;

  const focused = auths?.find((a) => a.id === focusId) ?? null;
  const openItems = openType ? (auths ?? []).filter((a) => a.instrument_type === openType) : [];

  return (
    <CommandLayout caseId={caseId} variant="manifesto" currentCommand="shield-wall">
      <header className="rounded border border-steel-dark bg-iron/70 px-6 py-6 backdrop-blur-sm">
        <p className="mb-2 font-display text-base font-light uppercase tracking-[0.18em] text-bone-mute">{`// ${lex.commands["shield-wall"].verb.toLowerCase()}`}</p>
        <h2 className="font-display text-2xl font-light leading-tight tracking-tight text-bone md:text-3xl"
            style={{ textShadow: "0 2px 16px rgba(0,0,0,0.6)" }}>
          Every action stands behind a wall — or it doesn't.
        </h2>
        <p className="mt-3 max-w-3xl font-display text-sm leading-relaxed text-bone-dim">
          Each contract, trust power, and license is a wall across time. Grants are grouped
          by instrument; open a band to see every grant, and a grant to see what it shields,
          its conditions, and what breaches it. A greyed grant is expired.
        </p>

        <div className="mt-5 flex flex-wrap items-center justify-end gap-3">
          <Link
            to={`/case/${caseId}/authority`}
            className="mr-auto rounded border border-ember-bright/50 bg-ember/10 px-3 py-1.5 font-display text-base font-light uppercase tracking-[0.14em] text-bone transition-colors hover:bg-ember/30 hover:text-ember-bright"
          >
            ⚖ authority verdicts (L3) ▸
          </Link>
          {status?.last_built && (
            <span className="font-display text-sm font-light uppercase tracking-[0.1em] text-bone-mute/70">
              wall raised {new Date(status.last_built).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
              {newSince > 0 ? ` · ${newSince} new instrument${newSince === 1 ? "" : "s"} since` : ""}
            </span>
          )}
          <button
            type="button"
            disabled={!caseId || building}
            onClick={() => build()}
            className={`rounded border px-3 py-1.5 font-display text-base font-light uppercase tracking-[0.14em] transition-colors ${
              building
                ? "border-ember-bright/40 bg-ember/10 text-ember-bright"
                : newSince > 0
                  ? "border-ember-bright/70 bg-ember/20 text-ember-bright shadow-ember-soft animate-pulse-ember"
                  : "border-ember-bright/50 bg-ember/10 text-bone hover:bg-ember/30 hover:text-ember-bright"
            }`}
          >
            {building ? lex.commands["shield-wall"].pending : `⛨ ${lex.commands["shield-wall"].action}`}
          </button>
        </div>
        {buildError && (
          <p className="mt-2 font-display text-base text-ember-bright">// build failed: {buildError}</p>
        )}
        {building && (
          <p className="mt-2 font-display text-sm font-light uppercase tracking-[0.1em] text-bone-mute/70">
            // re-reading every included instrument — trust, will, letters, powers of attorney, licenses…
          </p>
        )}
      </header>

      <div className="rounded border border-steel-dark bg-iron/70 backdrop-blur-sm">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-steel-dark/70 px-5 py-3">
          <span className="font-display text-base font-light uppercase tracking-[0.16em] text-bone-dim">{`${lex.commands["shield-wall"].noun.toLowerCase()} // authorities by instrument`}</span>
          <Legend />
        </div>
        <div className="px-4 py-5">
          {isLoading && <p className="font-display text-base text-bone-mute">// raising the walls…</p>}
          {error && <p className="font-display text-base text-ember-bright">// {error.message}</p>}
          {!isLoading && !error && (auths?.length ?? 0) === 0 && (
            <p className="font-display text-base text-bone-mute">{`// ${lex.ui.shieldWallEmpty}`}</p>
          )}
          {auths && auths.length > 0 && (
            <CategoryWall auths={auths} openType={openType} onOpen={(t) => { setOpenType(t); setFocusId(null); }} />
          )}
        </div>
      </div>

      {openType && openItems.length > 0 && (
        <CategoryPanel
          type={openType}
          items={openItems}
          onClose={() => setOpenType(null)}
          onFocus={(id) => setFocusId(id)}
        />
      )}

      {focused && (
        <AuthorityInfographic group={[focused]} onClose={() => setFocusId(null)} />
      )}
    </CommandLayout>
  );
}

/* ============================ status palette ============================ */

const STATUS_COLOR: Record<string, string> = {
  in_force: "#e8703a",
  condition_unmet: "#c9a23a",
  suspended: "#c9a23a",
  expired: "#6b7480",
  terminated: "#8a3b3b",
  not_yet_effective: "#5b9bd5",
  unknown: "#5a6470",
};
const sc = (k: string): string => STATUS_COLOR[k] ?? "#5a6470";
const statusLabel = (s: string): string => s.replace(/_/g, " ");

const TYPE_LABEL: Record<string, string> = {
  trust: "Trust Powers",
  will: "Will & Letters Testamentary",
  contract: "Contracts & Engagements",
  corporate_resolution: "Corporate Resolutions",
  power_of_attorney: "Powers of Attorney",
  license: "Licenses",
  permit: "Permits",
  statute: "Statutes",
  other: "Other Instruments",
};
const typeLabel = (t: string): string => TYPE_LABEL[t] ?? t.replace(/_/g, " ");

function Legend() {
  const items: [string, string][] = [
    ["in force", sc("in_force")],
    ["conditional", sc("condition_unmet")],
    ["expired (greyed)", sc("expired")],
    ["terminated", sc("terminated")],
  ];
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 font-display text-sm font-light uppercase tracking-[0.1em] text-bone-mute">
      {items.map(([label, c]) => (
        <span key={label} className="flex items-center gap-1.5">
          <i className="inline-block h-2.5 w-3.5 rounded-sm" style={{ background: c }} /> {label}
        </span>
      ))}
    </div>
  );
}

/* ============================ time helpers ============================ */

const DAY = 86400000;
function parse(d: string | null): number | null {
  if (!d) return null;
  const t = new Date(d).getTime();
  return isNaN(t) ? null : t;
}
function yr(ms: number): number { return new Date(ms).getUTCFullYear(); }

/* ============================ category band ============================ */

interface Band {
  type: string;
  items: Authority[];
  eff: number;
  exp: number | null; // null = open-ended
  inForce: number;
  expired: number;
  conditional: number;
}

function buildBands(auths: Authority[], fallbackLo: number): Band[] {
  const byType = new Map<string, Authority[]>();
  for (const a of auths) {
    const arr = byType.get(a.instrument_type) ?? [];
    arr.push(a);
    byType.set(a.instrument_type, arr);
  }
  const bands: Band[] = [];
  for (const [type, items] of byType) {
    const effs = items.map((a) => parse(a.effective_date)).filter((x): x is number => x !== null);
    const eff = effs.length ? Math.min(...effs) : fallbackLo;
    const anyOpen = items.some((a) => parse(a.expiration_date) === null);
    const exps = items.map((a) => parse(a.expiration_date)).filter((x): x is number => x !== null);
    const exp = anyOpen ? null : (exps.length ? Math.max(...exps) : null);
    bands.push({
      type, items, eff, exp,
      inForce: items.filter((a) => a.status === "in_force").length,
      expired: items.filter((a) => a.status === "expired" || a.status === "terminated").length,
      conditional: items.filter((a) => a.validity_basis === "conditional" || a.validity_basis === "until_event").length,
    });
  }
  return bands.sort((a, b) => a.eff - b.eff);
}

function CategoryWall({ auths, openType, onOpen }: { auths: Authority[]; openType: string | null; onOpen: (t: string) => void }) {
  const now = Date.now();
  const LABEL = 250, RIGHT = 992, TOP = 28, LANE = 54;

  const { d0, d1, bands } = useMemo(() => {
    const effs = auths.map((a) => parse(a.effective_date)).filter((x): x is number => x !== null);
    const exps = auths.map((a) => parse(a.expiration_date)).filter((x): x is number => x !== null);
    const lo = effs.length ? Math.min(...effs) : now - 365 * DAY;
    const hi = Math.max(now, exps.length ? Math.max(...exps) : now);
    const pad = Math.max((hi - lo) * 0.04, 120 * DAY);
    const d0 = lo - pad, d1 = hi + pad;
    return { d0, d1, bands: buildBands(auths, d0) };
  }, [auths, now]);

  const W = 1000, H = TOP + bands.length * LANE + 34;
  const x = (ms: number) => LABEL + ((ms - d0) / (d1 - d0)) * (RIGHT - LABEL);

  const years: number[] = [];
  for (let y = yr(d0); y <= yr(d1); y++) years.push(y);
  const everyN = years.length > 12 ? 2 : 1;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ maxHeight: H }}>
      <defs>
        <filter id="wallglow" x="-20%" y="-60%" width="140%" height="220%">
          <feGaussianBlur stdDeviation="2.4" result="b" />
          <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>

      {years.map((y, i) => {
        const ms = Date.UTC(y, 0, 1);
        if (ms < d0 || ms > d1) return null;
        const gx = x(ms);
        return (
          <g key={y}>
            <line x1={gx} y1={TOP - 6} x2={gx} y2={H - 24} stroke="#2a3038" strokeWidth={1} />
            {i % everyN === 0 && (
              <text x={gx} y={H - 10} textAnchor="middle" fontSize={9} fill="#6b7480" fontFamily="ui-monospace, monospace">{y}</text>
            )}
          </g>
        );
      })}

      {now >= d0 && now <= d1 && (
        <g>
          <line x1={x(now)} y1={TOP - 10} x2={x(now)} y2={H - 24} stroke="#e8e2d6" strokeWidth={1} strokeDasharray="2 3" opacity={0.5} />
          <text x={x(now)} y={TOP - 13} textAnchor="middle" fontSize={8} fill="#bcb6a8" fontFamily="ui-monospace, monospace">NOW</text>
        </g>
      )}

      {bands.map((band, i) => {
        const y = TOP + i * LANE;
        const openEnded = band.exp === null;
        const x1 = x(Math.max(band.eff, d0));
        const x2 = openEnded ? RIGHT : x(Math.min(band.exp!, d1));
        const w = Math.max(x2 - x1, 6);
        const anyInForce = band.inForce > 0;
        const color = anyInForce ? sc("in_force") : sc("expired");
        const hatch = band.conditional > 0;
        const isOpen = openType === band.type;
        const barY = y + 12, barH = 26;
        const hatchId = `bh${i}`;
        const summary = `${band.items.length} grant${band.items.length === 1 ? "" : "s"} · ${band.inForce} in force${band.expired ? ` · ${band.expired} expired` : ""}`;
        return (
          <g key={band.type} className="cursor-pointer" onClick={() => onOpen(band.type)} opacity={!openType || isOpen ? 1 : 0.5}>
            <text x={8} y={barY + 9} fontSize={12} fill={isOpen ? "#e8e2d6" : "#cbbfa6"} fontFamily="ui-monospace, monospace">
              {typeLabel(band.type)}
            </text>
            <text x={8} y={barY + 22} fontSize={8} fill="#6b7480" fontFamily="ui-monospace, monospace" className="uppercase">
              {summary}
            </text>

            <defs>
              <pattern id={hatchId} width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                <rect width="6" height="6" fill={color} opacity={0.32} />
                <line x1="0" y1="0" x2="0" y2="6" stroke={color} strokeWidth="2.4" />
              </pattern>
            </defs>
            <rect x={x1} y={barY} width={w} height={barH} rx={2}
              fill={hatch ? `url(#${hatchId})` : color}
              opacity={hatch ? 1 : 0.82}
              filter={anyInForce ? "url(#wallglow)" : undefined}
              stroke={isOpen ? "#e8e2d6" : color} strokeWidth={isOpen ? 1.6 : 0.5} />
            {brickLines(x1, w).map((bx, k) => (
              <line key={k} x1={bx} y1={barY} x2={bx} y2={barY + barH} stroke="#0c0f12" strokeWidth={0.6} opacity={0.4} />
            ))}
            {/* count badge */}
            <g transform={`translate(${x1 + 8}, ${barY + barH / 2})`}>
              <text x={0} y={4} fontSize={12} fontFamily="ui-monospace, monospace" fontWeight="600" fill="#0c0f12">{band.items.length}</text>
            </g>
            {openEnded && (
              <text x={RIGHT - 2} y={barY + 17} textAnchor="end" fontSize={12} fill="#0c0f12">▸</text>
            )}
            {/* affordance */}
            <text x={RIGHT} y={barY + barH + 12} textAnchor="end" fontSize={8} fill={isOpen ? "#e8703a" : "#6b7480"} fontFamily="ui-monospace, monospace" className="uppercase">
              {isOpen ? "open ▾" : "view grants ▸"}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function brickLines(x1: number, w: number): number[] {
  const out: number[] = [];
  const step = 26;
  for (let bx = x1 + step; bx < x1 + w - 4; bx += step) out.push(bx);
  return out;
}

/* ============================ category panel ============================ */

function fmtDate(d: string | null): string {
  if (!d) return "—";
  const t = new Date(d);
  return isNaN(t.getTime()) ? "—" : t.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

function CategoryPanel({ type, items, onClose, onFocus }: {
  type: string; items: Authority[]; onClose: () => void; onFocus: (id: string) => void;
}) {
  const sorted = [...items].sort((a, b) => (parse(a.effective_date) ?? 0) - (parse(b.effective_date) ?? 0));
  return (
    <div className="overflow-hidden rounded border border-steel-dark bg-iron/80 backdrop-blur-sm">
      <div className="flex items-center justify-between gap-4 border-b border-steel-dark/70 bg-black/30 px-6 py-4">
        <div>
          <p className="font-display text-base font-light uppercase tracking-[0.3em] text-bone-mute">instrument band</p>
          <h3 className="mt-1 font-display text-xl font-light text-bone">{typeLabel(type)} · {items.length}</h3>
        </div>
        <button onClick={onClose} className="font-display text-base font-light uppercase tracking-[0.2em] text-bone-mute hover:text-ember-bright">✕ close</button>
      </div>
      <div className="max-h-[520px] overflow-y-auto px-4 py-4">
        <ul className="space-y-2">
          {sorted.map((a) => <GrantRow key={a.id} a={a} onClick={() => onFocus(a.id)} />)}
        </ul>
      </div>
    </div>
  );
}

function GrantRow({ a, onClick }: { a: Authority; onClick: () => void }) {
  const color = sc(a.status);
  const expired = a.status === "expired" || a.status === "terminated";
  const window = a.validity_basis === "date_range"
    ? `${fmtDate(a.effective_date)} → ${a.expiration_date ? fmtDate(a.expiration_date) : "open"}`
    : a.validity_basis === "conditional"
      ? `${(a.conditions ?? []).length} condition${(a.conditions ?? []).length === 1 ? "" : "s"}`
      : a.validity_basis.replace(/_/g, " ");
  return (
    <li>
      <button
        onClick={onClick}
        className={`w-full rounded border bg-black/20 px-4 py-3 text-left transition-colors hover:border-ember-bright/50 hover:bg-ember/10 ${expired ? "border-steel-dark/60 opacity-70" : "border-steel-dark/70"}`}
      >
        <div className="flex items-start justify-between gap-3">
          <p className={`min-w-0 flex-1 font-display text-[13px] font-medium leading-snug ${expired ? "text-bone-mute" : "text-bone"}`}>
            {a.title}
          </p>
          <span className="shrink-0 rounded px-2 py-0.5 font-display text-sm font-light uppercase tracking-[0.08em]" style={{ background: color, color: "#0c0f12" }}>
            {statusLabel(a.status)}
          </span>
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 font-display text-sm font-light uppercase tracking-[0.14em] text-bone-mute">
          {a.grantee && <span>→ {a.grantee}</span>}
          <span>{window}</span>
          <span className="text-ember-soft/70">detail ▸</span>
        </div>
      </button>
    </li>
  );
}

export default ShieldWall;
